import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  Share2,
  PlusSquare,
  Download,
  Check,
  Copy,
  X,
  Sparkles,
  Wifi,
  ExternalLink,
  ShieldCheck,
  Layers,
  Terminal
} from 'lucide-react';

export default function MobileInstallModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('ios'); // 'ios', 'android', 'native'
  const [copied, setCopied] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentUrl(window.location.origin);
      // Check if already running in standalone PWA / full screen mode
      const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsStandalone(!!standalone);

      // Listen for Chrome Android beforeinstallprompt
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }
  }, []);

  if (!isOpen) return null;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleNativePwaInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } else {
      handleCopyUrl();
    }
  };

  return (
    <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 99999 }}>
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      
      <div className="mobile-install-modal-card glass-panel animate-scale-in">
        {/* Modal Header */}
        <div className="mobile-install-header">
          <div className="mobile-install-icon-badge">
            <Smartphone size={24} color="#007AFF" />
          </div>
          <div className="mobile-install-titles">
            <div className="mobile-install-kicker">
              <Sparkles size={12} color="#007AFF" />
              <span>Cross-Platform Mobile</span>
            </div>
            <h2 className="mobile-install-title">Run on Mobile (iOS & Android)</h2>
            <p className="mobile-install-sub">
              Access your full Expense Tracker, Voice AI & Password Vault on your phone.
            </p>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Device Segmented Control (iOS Apple Style) */}
        <div className="ios-segmented-control" role="tablist">
          <button
            type="button"
            className={`ios-segment-btn ${activeTab === 'ios' ? 'active' : ''}`}
            onClick={() => setActiveTab('ios')}
          >
            🍏 Apple iPhone (iOS)
          </button>
          <button
            type="button"
            className={`ios-segment-btn ${activeTab === 'android' ? 'active' : ''}`}
            onClick={() => setActiveTab('android')}
          >
            🤖 Android Phone
          </button>
          <button
            type="button"
            className={`ios-segment-btn ${activeTab === 'native' ? 'active' : ''}`}
            onClick={() => setActiveTab('native')}
          >
            ⚡ Native APK / Build
          </button>
        </div>

        {/* Quick URL Sharing Row */}
        <div className="mobile-url-box">
          <div className="mobile-url-info">
            <Wifi size={14} color="#10b981" />
            <span className="mobile-url-label">Open on your phone:</span>
            <code className="mobile-url-text">{currentUrl}</code>
          </div>
          <button type="button" className="mobile-copy-btn" onClick={handleCopyUrl}>
            {copied ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
        </div>

        {/* Tab 1: iOS iPhone Guide */}
        {activeTab === 'ios' && (
          <div className="mobile-tab-content animate-fade-in">
            <div className="mobile-steps-list">
              <div className="mobile-step-row">
                <div className="mobile-step-num">1</div>
                <div className="mobile-step-body">
                  <strong>Open in Safari on iPhone</strong>
                  <p>Open <code>{currentUrl}</code> in Safari on your iPhone or iPad.</p>
                </div>
              </div>

              <div className="mobile-step-row">
                <div className="mobile-step-num">2</div>
                <div className="mobile-step-body">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong>Tap Share Button</strong>
                    <Share2 size={16} color="#007AFF" />
                  </div>
                  <p>Tap the Share icon at the bottom toolbar of Safari (box with an arrow pointing up).</p>
                </div>
              </div>

              <div className="mobile-step-row">
                <div className="mobile-step-num">3</div>
                <div className="mobile-step-body">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong>Select "Add to Home Screen"</strong>
                    <PlusSquare size={16} color="#10b981" />
                  </div>
                  <p>Scroll down in the action sheet and tap <strong>"Add to Home Screen"</strong>, then tap <strong>"Add"</strong> in the top-right.</p>
                </div>
              </div>

              <div className="mobile-step-row">
                <div className="mobile-step-num">4</div>
                <div className="mobile-step-body">
                  <strong>Full Native Experience</strong>
                  <p>A native CipherVault app icon appears on your iPhone home screen. It launches full screen with no browser bars, supporting Face ID & offline access!</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Android Guide */}
        {activeTab === 'android' && (
          <div className="mobile-tab-content animate-fade-in">
            {deferredPrompt && (
              <div className="android-pwa-install-banner">
                <div>
                  <strong>Ready to Install Directly</strong>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Install the native PWA package with 1 click.</p>
                </div>
                <button type="button" className="et-btn et-btn-primary" onClick={handleNativePwaInstall}>
                  <Download size={15} /> Install App
                </button>
              </div>
            )}

            <div className="mobile-steps-list">
              <div className="mobile-step-row">
                <div className="mobile-step-num">1</div>
                <div className="mobile-step-body">
                  <strong>Open in Chrome on Android</strong>
                  <p>Open <code>{currentUrl}</code> in Google Chrome or your Android web browser.</p>
                </div>
              </div>

              <div className="mobile-step-row">
                <div className="mobile-step-num">2</div>
                <div className="mobile-step-body">
                  <strong>Tap "Install App" or Menu (⋮)</strong>
                  <p>Look for the automatic <strong>"Install App"</strong> banner at the bottom, or tap the three dots (⋮) in the top-right.</p>
                </div>
              </div>

              <div className="mobile-step-row">
                <div className="mobile-step-num">3</div>
                <div className="mobile-step-body">
                  <strong>Tap "Add to Home screen" / "Install"</strong>
                  <p>Confirm the prompt. Android packages the app with a home screen launcher icon and full offline background caching.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Native Build (Capacitor) */}
        {activeTab === 'native' && (
          <div className="mobile-tab-content animate-fade-in">
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 12px' }}>
              Your project includes <strong>Capacitor</strong> configuration (<code>capacitor.config.json</code>) to compile this exact website into an Android Studio project (APK) or Xcode iOS project:
            </p>

            <div className="terminal-code-snippet">
              <div className="terminal-snippet-head">
                <Terminal size={13} />
                <span>Commands to build native APK & iOS project:</span>
              </div>
              <pre className="terminal-snippet-body">
{`# 1. Build the production web bundle
npm run build --workspace=apps/web

# 2. Add Android or iOS native platform
npx cap add android
npx cap add ios

# 3. Sync & Open in Android Studio or Xcode
npx cap sync
npx cap open android   # Generates APK in Android Studio
npx cap open ios       # Builds IPA in Xcode`}
              </pre>
            </div>
          </div>
        )}

        {/* Modal Footer */}
        <div className="mobile-install-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.74rem', color: '#94a3b8' }}>
            <ShieldCheck size={14} color="#10b981" />
            <span>Encrypted local session • Zero tracking</span>
          </div>
          <button type="button" className="et-btn et-btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
